import { useUserStore } from '@/stores/user';
import {
  capitalizeFirstLetter,
  convertISODateToMMDDYYYY,
  displayFullName,
  formatCurrency,
  formatPhoneNumber,
} from '@/lib/utils';
import { useState } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { PasswordDialog } from '@/components/PasswordDialog';
import { Button } from '@/components/ui/button';
import KeyValueListDisplay from '@/components/KeyValueDisplay';
import { isDisabledDueToTimeout } from '@/utils/helper';
import {
  caseTypesWithWitnessFees,
  SERVE_TYPE,
  SESSION_ENABLE_TIME,
  WorkOrderVendorActionOptions,
  WorkOrderVendorAfterActionOptions,
} from '@/utils/constant';
import {
  Action,
  AfterAction,
  PaymentType,
  WorkOrderPayment,
  type WorkOrderGetByIdResponse,
} from '@/types/workOrder.types';
import { PaymentStatusBadge } from '../steppers/WorkOrderPaymentForm';
import { Badge } from '@/components/ui/badge';

const getChecksInformation = (check: WorkOrderPayment) => {
  return [
    { label: 'Date', value: check?.issueDate ? convertISODateToMMDDYYYY(check.issueDate) : '-' },
    { label: 'Reference', value: check?.reference ?? '-' },
    { label: 'Issued To', value: check?.issuedTo ?? '-' },
    { label: 'Check #', value: check?.checkNumber != null ? String(check.checkNumber) : '-' },
    { label: 'Amount', value: check?.amount != null ? formatCurrency(check.amount) : '-' },
  ];
};

export const WoDetailsPeContent = ({ data }: { data: WorkOrderGetByIdResponse | undefined }) => {
  const session = useUserStore((state) => state.validateSession);
  const [showSSN, setShowSSN] = useState<boolean>(false);
  const [showDl, setShowDl] = useState<boolean>(false);
  const [openPasswordDialogFor, setOpenPasswordDialogFor] = useState<'SSN' | 'DL' | undefined>(undefined);

  const WoDetails = [
    {
      label: 'Type',
      value: SERVE_TYPE.find((type) => type.value === data?.type)?.label ?? '-',
    },
    { label: 'Person to Serve', value: data?.toServe ?? '-' },
    { label: 'Address Type', value: capitalizeFirstLetter(data?.addressType) ?? '-' },
    {
      label: 'Address',
      value: [data?.address1, data?.address2, data?.city, data?.state + ' ' + data?.zipCode].filter(Boolean).join(', '),
    },
    { label: 'Phone', value: data?.phone ? formatPhoneNumber(data?.phone) : '-' },
    { label: 'Affidavit Type', value: data?.affidavitType?.name ?? '-' },
    ...(data?.server
      ? [
          {
            label: 'Server Name',
            value: displayFullName(data?.server ?? undefined),
          },
        ]
      : []),
    { label: 'Server Instructions', value: data?.serverInstructions ?? 'No Instructions' },
    { label: 'Team', value: data?.team?.name ?? '-' },
    { label: 'Assigned User', value: displayFullName(data?.user ?? undefined) ?? '-' },
  ];

  const privateInformation = [
    { label: 'Social Security Number', value: data?.privateInfo?.ssn ?? '', field: 'SSN' },
    { label: 'Date of Birth', value: (data?.privateInfo?.dateOfBirth as unknown as string) ?? '', field: 'DATE' },
    { label: 'Driver License', value: data?.privateInfo?.dlNumber ?? '', field: 'DL' },
    { label: 'Others', value: data?.privateInfo?.others ?? '', field: 'OTHERS' },
  ];

  const getVendorInformation = () => {
    const vendor = data?.vendor;
    return [
      { label: 'Vendor Name', value: vendor?.vendor?.name ?? '-' },
      { label: 'Vendor Contact Name', value: vendor?.vendorContact?.contactName ?? '-' },
      {
        label: 'Service Action',
        value:
          vendor?.action === Action.OTHER
            ? vendor.otherAction
            : WorkOrderVendorActionOptions.find((action) => action.value === vendor?.action)?.label,
      },
      {
        label: 'After Service Action',
        value: vendor?.afterAction?.length
          ? [
              ...vendor.afterAction
                .filter((action) => action !== AfterAction.OTHER)
                .map((action) => WorkOrderVendorAfterActionOptions.find((opt) => opt.value === action)?.label),
              ...(vendor.afterAction.includes(AfterAction.OTHER) && vendor.otherAfterAction
                ? [vendor.otherAfterAction]
                : []),
            ].join(', ')
          : '-',
      },
      { label: 'Amount', value: formatCurrency(vendor?.amount ?? 0) },
      { label: 'Tracking Number', value: vendor?.trackingNumber ?? '-' },
    ];
  };

  const toggleView = (toggleFor: string) => {
    const currentTime = Date.now();
    if ((toggleFor === 'SSN' && !showSSN) || (toggleFor === 'DL' && !showDl)) {
      if ((session?.time && currentTime - session.time > SESSION_ENABLE_TIME) ?? !session) {
        toggleFor === 'SSN' ? setOpenPasswordDialogFor('SSN') : setOpenPasswordDialogFor('DL');
      } else {
        if (session?.status === 'SUCCESS') toggleFor === 'SSN' ? setShowSSN(!showSSN) : setShowDl(!showDl);
      }
    } else {
      if (session?.status === 'SUCCESS') toggleFor === 'SSN' ? setShowSSN(!showSSN) : setShowDl(!showDl);
    }
  };

  const formatSSN = (ssn: string) => {
    const cleanedSSN = ssn.replace(/\D/g, '');
    const formattedSSN = cleanedSSN.replace(/^(\d{3})(\d{2})(\d{4})$/, '$1-$2-$3');
    return formattedSSN;
  };

  const witnessFeeChecks =
    data?.workOrderPayments?.filter((payment) => payment.paymentType === PaymentType.WITNESS_FEE) ?? [];
  const operatingChecks =
    data?.workOrderPayments?.filter((payment) => payment.paymentType === PaymentType.OPERATING_CHECK) ?? [];

  return (
    <div className="flex flex-col gap-4 w-full px-6 py-4">
      <div className="flex flex-col gap-2 w-full text-sm whitespace-pre-wrap">
        <KeyValueListDisplay data={WoDetails} />
      </div>
      <hr />

      {data?.vendor && (
        <>
          <div className="flex flex-col gap-4">
            <span className="text-sm font-semibold">Vendor Information</span>
          </div>
          <div className="flex flex-col gap-2 w-full text-sm whitespace-pre-wrap">
            <KeyValueListDisplay data={getVendorInformation()} />
          </div>
          <hr />
        </>
      )}

      {data?.type === 'INDIVIDUAL' && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-row justify-between items-center w-full">
            <span className="text-sm font-semibold">Private Information</span>
            {!privateInformation.some((info) => info.value) && (
              <span className="text-sm">No</span>
            )}
          </div>
          {privateInformation.some((info) => info.value) &&
            privateInformation.map((info, index) => (
              <div key={index} className="flex flex-row justify-between items-start text-sm">
                <div className="font-medium flex-shrink-0 w-[50%]">{info.label}</div>
                <div className="text-end font-normal w-[50%] break-words flex flex-row justify-end gap-2 items-center">
                  {info.value
                    ? info.field === 'SSN'
                      ? !session || !showSSN
                        ? `XXX-XX-****`
                        : formatSSN(info.value)
                      : info.field === 'DATE'
                        ? convertISODateToMMDDYYYY(info.value)
                        : info.field === 'DL'
                          ? !session || !showDl
                            ? '************'
                            : info.value
                          : info.value
                    : '-'}
                  {info.value && (info.field === 'SSN' || info.field === 'DL') ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="p-0 h-4 w-4 hover:bg-white"
                      disabled={isDisabledDueToTimeout()}
                      tabIndex={-1}
                    >
                      {(info.field === 'SSN' && showSSN) || (info.field === 'DL' && showDl) ? (
                        <EyeIcon className="cursor-pointer" onClick={() => toggleView(info.field)} size={16} />
                      ) : (
                        <EyeOffIcon className="cursor-pointer" onClick={() => toggleView(info.field)} size={16} />
                      )}
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          <hr />
        </div>
      )}

      {!!witnessFeeChecks.length &&
        data?.service.case.typeId &&
        caseTypesWithWitnessFees.includes(data?.service.case.typeId) && (
          <ChecksDetails checks={witnessFeeChecks} header="Witness Fee Check" showStatusBadge />
        )}

      {!!operatingChecks.length && <ChecksDetails checks={operatingChecks} header="Operating Checks" showStatusBadge />}

      <div className="flex flex-col gap-4">
        <span className="text-sm font-semibold">Descriptions of Document</span>
        <div className="font-normal text-sm flex flex-col gap-2">
          {data?.documents.map((doc, index) => <span key={index}>{doc?.caseDocType?.docType?.name}</span>)}
        </div>
      </div>
      {openPasswordDialogFor && (
        <PasswordDialog
          cancelBtnClick={() => setOpenPasswordDialogFor(undefined)}
          confirmBtnClick={() => {
            if (openPasswordDialogFor === 'SSN') setShowSSN(true);
            if (openPasswordDialogFor === 'DL') setShowDl(true);
            setOpenPasswordDialogFor(undefined);
          }}
        />
      )}
    </div>
  );
};

const ChecksDetails = ({
  checks,
  header,
  showStatusBadge,
}: {
  checks: WorkOrderPayment[];
  header: string;
  showStatusBadge?: boolean;
}) => {
  return (
    <>
      <div className="flex flex-col gap-4">
        <span className="text-sm font-semibold">{header}</span>
      </div>
      <div className="flex flex-col gap-4">
        {checks.map((payment, index) => {
          const badge = payment.status ? PaymentStatusBadge[payment.status] : undefined;
          return (
            <div key={index} className="flex flex-col gap-2 w-full text-sm whitespace-pre-wrap">
              <div className={`flex justify-between items-center ${checks.length > 1 ? 'pb-2' : ''}`}>
                {checks.length > 1 && <span className="text-xs font-medium text-gray-500">Check #{index + 1}</span>}
                {badge && showStatusBadge && (
                  <Badge
                    style={{
                      backgroundColor: badge.bgColor,
                      color: badge.fontColor,
                    }}
                  >
                    {badge.label}
                  </Badge>
                )}
              </div>
              <KeyValueListDisplay data={getChecksInformation(payment)} />
              {index < checks.length - 1 && <hr className="border-dashed" />}
            </div>
          );
        })}
      </div>
      <hr />
    </>
  );
};
